import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { useHaptic } from '@/app/TmaProvider';
import { Card, ListRow, MainButton } from '@/components/ui';
import { cn } from '@/lib/cn';
import { texts } from '@/lib/texts';

import { OnboardingScreen, OptionsState, useOptions } from './shared';

const t = texts.onboarding.city;

function Radio({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded-full border-2',
        on ? 'border-accent' : 'border-border',
      )}
    >
      {on ? <span className="bg-accent h-3 w-3 rounded-full" /> : null}
    </span>
  );
}

/** Шаг 1 — город (BLUEPRINT 3.2). Один город на старте — выбран сразу. */
export function CityScreen() {
  return (
    <OnboardingScreen step={1} title={t.title} subtitle={t.subtitle}>
      {({ draft, update }) => <Body draft={draft} update={update} />}
    </OnboardingScreen>
  );
}

function Body({
  draft,
  update,
}: {
  draft: { cityId?: string };
  update: (p: { cityId?: string; districtIds?: string[]; step?: number }) => void;
}) {
  const options = useOptions();
  const navigate = useNavigate();
  const haptic = useHaptic();
  const citiesData = options.data?.cities;
  const cities = citiesData ?? [];
  const selected = draft.cityId ?? (cities.length === 1 ? cities[0]?.id : undefined);

  useEffect(() => {
    const only = citiesData?.length === 1 ? citiesData[0] : undefined;
    if (!draft.cityId && only) update({ cityId: only.id });
  }, [citiesData, draft.cityId, update]);

  const choose = (id: string) => {
    haptic.selection();
    if (id !== draft.cityId) update({ cityId: id, districtIds: [] });
  };

  // Открытых городов может не быть вовсе: набор идёт только там, где ops прошёл чек-лист
  // открытия. Без этой ветки первый шаг рисовался пустым — ни вариантов, ни кнопки, ни
  // объяснения, и человек упирался в тупик, не понимая, сломано это или так задумано.
  if (!options.isPending && cities.length === 0) {
    return (
      <OptionsState query={options}>
        <Card>
          <p className="text-muted text-sm">{t.empty}</p>
        </Card>
      </OptionsState>
    );
  }

  return (
    <OptionsState query={options}>
      <Card flush>
        {cities.map((c) => (
          <ListRow
            key={c.id}
            title={c.name}
            leading={<Radio on={selected === c.id} />}
            onClick={() => choose(c.id)}
          />
        ))}
      </Card>
      {cities.length === 1 ? <p className="text-muted px-1 text-sm">{t.singleCityHint}</p> : null}
      <MainButton
        text={texts.common.next}
        enabled={Boolean(selected)}
        onClick={() => {
          if (!selected) return;
          update({ cityId: selected, step: 1 });
          void navigate('/onboarding/district');
        }}
      />
    </OptionsState>
  );
}
